import { fetchApi, ApiError, ensureCurrentMediaSession, getApiUrl } from '@/api/client';
import { getAccessToken } from '@/lib/tokenStorage';
import { normalizePrivateMediaUrl } from '@/lib/media-url';
import type { CameraResponse, CameraMapPositionData, RecordingResponse, RecordingPlaybackResponse, RecordingDownloadResponse } from '@/types/camera';
export type { RecordingResponse } from '@/types/camera';

export async function getCameras(siteId?: string, zoneId?: string): Promise<{ items: CameraResponse[]; total: number }> {
  const params = new URLSearchParams();
  if (siteId) params.append('site_id', siteId);
  if (zoneId) params.append('zone_id', zoneId);
  const query = params.toString() ? `?${params}` : '';
  return fetchApi<{ items: CameraResponse[]; total: number }>(`/api/v1/cameras${query}`);
}

export async function getCamera(cameraId: string): Promise<CameraResponse> {
  return fetchApi<CameraResponse>(`/api/v1/cameras/${cameraId}`);
}

export async function getCameraStream(
  cameraId: string,
  streamType: 'live' | 'ai' = 'live',
): Promise<{ camera_id: string; stream_url: string; stream_type: string; expires_at: string | null }> {
  const params = streamType !== 'live' ? `?stream_type=${streamType}` : '';
  return fetchApi<{ camera_id: string; stream_url: string; stream_type: string; expires_at: string | null }>(`/api/v1/cameras/${cameraId}/stream${params}`);
}

export async function createCamera(data: {
  name: string;
  zone_id: string;
  stream_url?: string | null;
  rtsp_url?: string | null;
  rtsp_channel?: number | null;
  ptz_enabled?: boolean;
  floor_id?: string | null;
}): Promise<CameraResponse> {
  return fetchApi<CameraResponse>('/api/v1/cameras', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCamera(cameraId: string, data: {
  name?: string | null;
  zone_id?: string | null;
  floor_id?: string | null;
  stream_url?: string | null;
  rtsp_url?: string | null;
  rtsp_channel?: number | null;
  r2_path?: string | null;
  status?: string | null;
  ptz_enabled?: boolean | null;
  map_position?: CameraMapPositionData | null;
}): Promise<CameraResponse> {
  return fetchApi<CameraResponse>(`/api/v1/cameras/${cameraId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCamera(cameraId: string): Promise<void> {
  await fetchApi(`/api/v1/cameras/${cameraId}`, { method: 'DELETE' });
}

export interface CameraStatusSummary {
  total: number;
  online: number;
  offline: number;
  maintenance: number;
}

export async function getCameraStatus(siteId?: string): Promise<CameraStatusSummary> {
  const query = siteId ? `?site_id=${siteId}` : '';
  return fetchApi<CameraStatusSummary>(`/api/v1/cameras/status${query}`);
}

export async function getCameraRecordings(cameraId: string, date?: string, limit?: number): Promise<RecordingResponse[]> {
  const params = new URLSearchParams();
  if (date) params.append('date', date);
  if (limit) params.append('limit', String(limit));
  const query = params.toString() ? `?${params}` : '';
  return fetchApi<RecordingResponse[]>(`/api/v1/cameras/${cameraId}/recordings${query}`);
}

export async function getRecordingPlaybackUrl(cameraId: string, date: string, time: string): Promise<RecordingPlaybackResponse> {
  return fetchApi(`/api/v1/cameras/${cameraId}/recordings/${date}/${time}`);
}

export async function downloadRecording(cameraId: string, recordingId: string): Promise<void> {
  const data = await fetchApi<RecordingDownloadResponse>(`/api/v1/cameras/${cameraId}/recordings/${recordingId}/download`);
  await ensureCurrentMediaSession();
  const accessToken = getAccessToken();
  const downloadUrl = normalizePrivateMediaUrl(data.download_url, getApiUrl());
  if (!downloadUrl) throw new ApiError('녹화 다운로드 주소가 없습니다.', 502);
  const response = await fetch(downloadUrl, { credentials: 'include', cache: 'no-store' });
  if (!response.ok) {
    if (response.status === 403) window.dispatchEvent(new CustomEvent('cctv:permission-denied'));
    throw new ApiError('녹화 다운로드에 실패했습니다.', response.status);
  }
  const blob = await response.blob();
  if (!accessToken || getAccessToken() !== accessToken) throw new ApiError('로그인 세션이 변경되었습니다.', 409);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `recording-${recordingId}.mp4`;
  a.click();
  URL.revokeObjectURL(url);
}

export interface PtzCommand {
  action: 'move' | 'stop' | 'preset';
  direction?: 'up' | 'down' | 'left' | 'right' | 'zoom_in' | 'zoom_out';
  speed?: number;
  preset_id?: number;
}

export async function sendPtzCommand(cameraId: string, command: PtzCommand): Promise<{ status: string }> {
  return fetchApi<{ status: string }>(`/api/v1/cameras/${cameraId}/ptz`, {
    method: 'POST',
    body: JSON.stringify(command),
  });
}
